import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Skeleton, Alert, Button, Chip,
} from '@mui/material';
import {
  WbSunny as SunIcon,
  TrendingUp as UpIcon,
  Shield as ShieldIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowIcon,
  Circle as DotIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/* ── Shared styles ── */
const CARD = { boxShadow: 'none', border: '1px solid #e2e8f0', borderRadius: 2 };
const HEAD = { px: 2, py: 1.25, borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 1 };

/* ── Color maps ── */
const REGIME_CLR = {
  RISK_OFF: '#e53935', RECOVERY_MIXED: '#fb8c00', NARROW_RALLY: '#fdd835',
  RISK_ON: '#43a047', OVERHEAT: '#d32f2f', ROTATION: '#00838f',
};
const LIGHT_CLR = { GREEN: '#4caf50', YELLOW: '#ff9800', RED: '#f44336' };
const STATE_CLR = {
  PANIC: '#d32f2f', BOTTOMING: '#e65100', NEUTRAL: '#757575',
  RISK_ON: '#2e7d32', DISTRIBUTION: '#e65100', EUPHORIA: '#c62828',
};
const ALERT_CLR = {
  LOW: '#4caf50', MEDIUM: '#ff9800', HIGH: '#f44336', EXTREME: '#b71c1c',
  GREEN: '#4caf50', YELLOW: '#ff9800', RED: '#f44336',
};

/* ═══════════════════════════════════════════════════
   MORNING BRIEF PAGE
   ═══════════════════════════════════════════════════ */
export default function MorningBrief() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem('indicatorai_token') || localStorage.getItem('indicatorai_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/morning-brief`, { headers });
      setData(res.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <BriefSkeleton />;

  const { allocation, vnindex, sentiment, market_risk, correction, top_picks, regime_alerts, sectors } = data || {};

  return (
    <Box sx={{ px: 2, py: 2, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 42, height: 42, borderRadius: 1.5, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SunIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
              {t('morningBrief.title', 'Morning Brief')}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {allocation?.trade_date || vnindex?.trade_date || '—'}
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={fetchData}
          sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px', color: '#64748b', borderColor: '#e2e8f0',
            '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' } }}>
          {t('morningBrief.refresh', 'Refresh')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Row 1 — Market Pulse: Regime + VNI + Sentiment + Risk */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <PulseCard allocation={allocation} vnindex={vnindex} navigate={navigate} />
        <SentimentCard sentiment={sentiment} navigate={navigate} />
        <RiskCard market_risk={market_risk} correction={correction} navigate={navigate} />
      </Box>

      {/* Row 2 — Allocation Strip */}
      {allocation && <AllocationStrip allocation={allocation} navigate={navigate} />}

      {/* Row 3 — Top Picks + Sectors + Alerts */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        <Box sx={{ flex: '1 1 55%', minWidth: 0 }}>
          <TopPicksCard picks={top_picks} navigate={navigate} />
        </Box>
        <Box sx={{ flex: '1 1 45%', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <SectorsCard sectors={sectors} navigate={navigate} />
          <AlertsCard alerts={regime_alerts} />
        </Box>
      </Box>
    </Box>
  );
}


/* ═══════════════════════════════════════════════════
   PULSE — Regime + VNIndex
   ═══════════════════════════════════════════════════ */
function PulseCard({ allocation, vnindex, navigate }) {
  const { t } = useTranslation();
  const regime = allocation?.regime || 'UNKNOWN';
  const rClr = REGIME_CLR[regime] || '#757575';
  const chg = vnindex?.change_pct ?? 0;

  return (
    <Card elevation={0} sx={{ flex: '1 1 38%', minWidth: 240, ...CARD }}>
      <Box sx={HEAD}>
        <SpeedIcon sx={{ fontSize: 18, color: '#64748b' }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
          {t('morningBrief.pulse', 'Market Pulse')}
        </Typography>
      </Box>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* Regime */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: rClr, flexShrink: 0 }} />
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: rClr }}>
            {t(`concentrationAllocation.regime.${regime}`, regime)}
          </Typography>
          {allocation?.regime_days > 0 && (
            <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: rClr, opacity: 0.7, bgcolor: `${rClr}10`, px: 0.75, py: 0.15, borderRadius: '4px' }}>
              {t('morningBrief.dayN', { n: allocation.regime_days }, `Ngày ${allocation.regime_days}`)}
            </Typography>
          )}
        </Box>

        {/* VNIndex */}
        {vnindex && (
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1.5 }}>
            <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 500 }}>VN-Index</Typography>
            <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, fontFamily: 'monospace', color: '#1e293b' }}>
              {vnindex.close?.toLocaleString('en', { maximumFractionDigits: 1 })}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: chg >= 0 ? '#4caf50' : '#f44336' }}>
              {chg >= 0 ? '+' : ''}{chg?.toFixed(2)}%
            </Typography>
          </Box>
        )}

        {/* Traffic Lights */}
        {allocation?.traffic_lights && (
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {Object.entries(allocation.traffic_lights).map(([k, v]) => (
              <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: LIGHT_CLR[v] || '#bdbdbd' }} />
                <Typography sx={{ fontSize: '0.65rem', color: '#64748b', fontWeight: 500, textTransform: 'capitalize' }}>
                  {t(`morningBrief.light.${k}`, k)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/concentration')}
          sx={{ mt: 1.5, p: 0, minWidth: 0, fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'none' }}>
          {t('morningBrief.viewDetail', 'Xem chi tiết')}
        </Button>
      </CardContent>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   SENTIMENT — MSI Fear/Greed
   ═══════════════════════════════════════════════════ */
function SentimentCard({ sentiment, navigate }) {
  const { t } = useTranslation();
  if (!sentiment) return null;
  const msi = sentiment.msi ?? 0;
  const state = sentiment.market_state || 'NEUTRAL';
  const sClr = STATE_CLR[state] || '#757575';
  const gaugeColor = msi >= 60 ? '#4caf50' : msi >= 40 ? '#ff9800' : '#f44336';

  return (
    <Card elevation={0} sx={{ flex: '1 1 32%', minWidth: 200, ...CARD }}>
      <Box sx={HEAD}>
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: sClr }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
          {t('morningBrief.sentiment', 'Sentiment')}
        </Typography>
        <Chip label={t(`morningBrief.state.${state}`, state)} size="small"
          sx={{ ml: 'auto', height: 20, fontSize: '0.62rem', fontWeight: 700, color: sClr, bgcolor: `${sClr}10`, borderRadius: '4px' }} />
      </Box>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        {/* MSI gauge */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Box sx={{ position: 'relative', width: 52, height: 52 }}>
            <svg width="52" height="52" viewBox="0 0 52 52">
              <circle cx="26" cy="26" r="22" fill="none" stroke="#f1f5f9" strokeWidth="4" />
              <circle cx="26" cy="26" r="22" fill="none" stroke={gaugeColor} strokeWidth="4"
                strokeDasharray={`${(msi / 100) * 138.2} 138.2`}
                strokeLinecap="round" transform="rotate(-90 26 26)" />
            </svg>
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: gaugeColor, fontFamily: 'monospace' }}>
                {msi?.toFixed(0)}
              </Typography>
            </Box>
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>{t('morningBrief.fearScore', 'Fear Score')}</Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace' }}>
              {sentiment.fear_score?.toFixed(0) ?? '—'}
            </Typography>
            <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8', mt: 0.5 }}>{t('morningBrief.exposureTarget', 'Target Exposure')}</Typography>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace' }}>
              {sentiment.exposure_target != null ? `${(sentiment.exposure_target * 100).toFixed(0)}%` : '—'}
            </Typography>
          </Box>
        </Box>
        <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/sentiment')}
          sx={{ p: 0, minWidth: 0, fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'none' }}>
          {t('morningBrief.viewDetail', 'Xem chi tiết')}
        </Button>
      </CardContent>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   RISK — Unified Risk + Correction
   ═══════════════════════════════════════════════════ */
function RiskCard({ market_risk, correction, navigate }) {
  const { t } = useTranslation();
  const risk = market_risk?.risk_score ?? 0;
  const alert = market_risk?.alert_level || 'LOW';
  const aClr = ALERT_CLR[alert] || '#757575';
  const flags = market_risk?.active_flags || [];

  return (
    <Card elevation={0} sx={{ flex: '1 1 30%', minWidth: 200, ...CARD }}>
      <Box sx={HEAD}>
        <ShieldIcon sx={{ fontSize: 18, color: aClr }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>
          {t('morningBrief.risk', 'Risk')}
        </Typography>
        <Chip label={alert} size="small"
          sx={{ ml: 'auto', height: 20, fontSize: '0.62rem', fontWeight: 700, color: '#fff', bgcolor: aClr, borderRadius: '4px' }} />
      </Box>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 1 }}>
          <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, fontFamily: 'monospace', color: aClr }}>{risk?.toFixed(0)}</Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>/100</Typography>
        </Box>

        {/* Active flags */}
        {flags.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
            {flags.map(f => (
              <Typography key={f} sx={{ fontSize: '0.58rem', fontWeight: 600, color: '#e53935', bgcolor: '#fef2f2', px: 0.5, py: 0.15, borderRadius: '3px' }}>
                ⚠ {f}
              </Typography>
            ))}
          </Box>
        )}

        {/* Correction */}
        {correction && (
          <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
            <Box>
              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{t('morningBrief.correctionRisk', 'Pullback 5d')}</Typography>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace' }}>{correction.p_pullback_5d != null ? `${(correction.p_pullback_5d * 100).toFixed(0)}%` : '—'}</Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8' }}>{t('morningBrief.drawdown', 'Expected DD')}</Typography>
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: '#e53935' }}>{correction.expected_drawdown != null ? `${(correction.expected_drawdown * 100).toFixed(1)}%` : '—'}</Typography>
            </Box>
          </Box>
        )}

        <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/market-risk')}
          sx={{ mt: 1, p: 0, minWidth: 0, fontSize: '0.68rem', fontWeight: 600, color: '#64748b', textTransform: 'none' }}>
          {t('morningBrief.viewDetail', 'Xem chi tiết')}
        </Button>
      </CardContent>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   ALLOCATION STRIP — Cash / Long / Short bar
   ═══════════════════════════════════════════════════ */
function AllocationStrip({ allocation, navigate }) {
  const { t } = useTranslation();
  const { long_pct = 0, short_pct = 0, cash_pct = 0 } = allocation;
  const parts = [
    { key: 'long', value: long_pct, color: '#388e3c', bg: '#e8f5e9' },
    { key: 'short', value: short_pct, color: '#c62828', bg: '#ffebee' },
    { key: 'cash', value: cash_pct, color: '#546e7a', bg: '#eceff1' },
  ].filter(x => x.value > 0);

  return (
    <Card elevation={0} sx={{ mb: 2, ...CARD, cursor: 'pointer' }} onClick={() => navigate('/concentration')}>
      <CardContent sx={{ py: 1.25, px: 2, '&:last-child': { pb: 1.25 }, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', flexShrink: 0 }}>
          {t('morningBrief.allocation', 'Phân bổ')}
        </Typography>
        <Box sx={{ flex: 1, display: 'flex', height: 24, borderRadius: '6px', overflow: 'hidden' }}>
          {parts.map(p => (
            <Box key={p.key} sx={{ width: `${p.value}%`, bgcolor: p.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: p.value > 8 ? 0 : 20 }}>
              <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: p.color }}>
                {t(`morningBrief.alloc.${p.key}`, p.key)} {p.value.toFixed(0)}%
              </Typography>
            </Box>
          ))}
        </Box>
        {/* Gates */}
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          {[
            { key: 'bluechip', open: allocation.gates?.bluechip_open },
            { key: 'midcap', open: allocation.gates?.midcap_open },
          ].map(g => (
            <Box key={g.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: g.open ? '#4caf50' : '#bdbdbd' }} />
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: g.open ? '#2e7d32' : '#94a3b8' }}>
                {t(`morningBrief.gate.${g.key}`, g.key)}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   TOP PICKS — BUY signals from detector
   ═══════════════════════════════════════════════════ */
function TopPicksCard({ picks, navigate }) {
  const { t } = useTranslation();
  if (!picks || picks.length === 0) {
    return (
      <Card elevation={0} sx={{ ...CARD, height: '100%' }}>
        <Box sx={HEAD}>
          <UpIcon sx={{ fontSize: 18, color: '#4caf50' }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{t('morningBrief.topPicks', 'Top Picks')}</Typography>
        </Box>
        <CardContent sx={{ p: 2, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>{t('morningBrief.noSignals', 'Không có tín hiệu hôm nay')}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card elevation={0} sx={{ ...CARD, height: '100%' }}>
      <Box sx={{ ...HEAD, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UpIcon sx={{ fontSize: 18, color: '#4caf50' }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{t('morningBrief.topPicks', 'Top Picks')}</Typography>
          <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>({picks.length})</Typography>
        </Box>
        <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/detector-recommendations')}
          sx={{ p: 0, minWidth: 0, fontSize: '0.65rem', fontWeight: 600, color: '#64748b', textTransform: 'none' }}>
          {t('morningBrief.viewAll', 'Xem tất cả')}
        </Button>
      </Box>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
              {['Mã', 'Setup', 'Entry', 'Target', 'Stop', 'R:R', 'Score'].map(h => (
                <th key={h} style={{ padding: '6px 10px', textAlign: h === 'Mã' ? 'left' : 'right', fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {picks.map((p, i) => (
              <tr key={p.ticker} style={{ borderBottom: i < picks.length - 1 ? '1px solid #f8fafc' : 'none' }}>
                <td style={{ padding: '8px 10px' }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e293b' }}>{p.ticker}</Typography>
                  <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.company_name}</Typography>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: '#64748b' }}>{p.setup_type}</Typography>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, fontFamily: 'monospace' }}>{p.entry_price?.toLocaleString()}</Typography>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, fontFamily: 'monospace', color: '#4caf50' }}>{p.target?.toLocaleString()}</Typography>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, fontFamily: 'monospace', color: '#f44336' }}>{p.stop_loss?.toLocaleString()}</Typography>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, fontFamily: 'monospace', color: (p.risk_reward ?? 0) >= 2 ? '#4caf50' : '#ff9800' }}>
                    {p.risk_reward?.toFixed(1) ?? '—'}
                  </Typography>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, fontFamily: 'monospace', color: '#1976d2' }}>
                    {p.quality_score?.toFixed(0) ?? '—'}
                  </Typography>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   SECTORS — Leading / Lagging
   ═══════════════════════════════════════════════════ */
function SectorsCard({ sectors, navigate }) {
  const { t } = useTranslation();
  if (!sectors || (!sectors.leading?.length && !sectors.lagging?.length)) return null;

  return (
    <Card elevation={0} sx={CARD}>
      <Box sx={HEAD}>
        <DotIcon sx={{ fontSize: 10, color: '#1976d2' }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{t('morningBrief.sectors', 'Sector Rotation')}</Typography>
        <Button size="small" endIcon={<ArrowIcon sx={{ fontSize: 14 }} />} onClick={() => navigate('/sector-rotation')}
          sx={{ ml: 'auto', p: 0, minWidth: 0, fontSize: '0.65rem', fontWeight: 600, color: '#64748b', textTransform: 'none' }}>
          {t('morningBrief.viewDetail', 'Xem chi tiết')}
        </Button>
      </Box>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Leading */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#4caf50', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              {t('morningBrief.leading', 'Dẫn dắt')}
            </Typography>
            {sectors.leading?.map(s => (
              <Box key={s.sector} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{s.sector}</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#4caf50', fontFamily: 'monospace' }}>
                  {s.rs?.toFixed(1)}
                </Typography>
              </Box>
            ))}
          </Box>
          {/* Lagging */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: '#f44336', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>
              {t('morningBrief.lagging', 'Yếu nhất')}
            </Typography>
            {sectors.lagging?.map(s => (
              <Box key={s.sector} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 500 }}>{s.sector}</Typography>
                <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#f44336', fontFamily: 'monospace' }}>
                  {s.rs?.toFixed(1)}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   ALERTS — Recent regime changes
   ═══════════════════════════════════════════════════ */
function AlertsCard({ alerts }) {
  const { t } = useTranslation();
  if (!alerts || alerts.length === 0) return null;

  return (
    <Card elevation={0} sx={CARD}>
      <Box sx={HEAD}>
        <WarningIcon sx={{ fontSize: 18, color: '#ff9800' }} />
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{t('morningBrief.alerts', 'Alerts')}</Typography>
      </Box>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {alerts.map((a, i) => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: i < alerts.length - 1 ? 0.75 : 0 }}>
            <Box sx={{ width: 4, height: 20, borderRadius: 2, bgcolor: a.severity === 'HIGH' ? '#f44336' : a.severity === 'MEDIUM' ? '#ff9800' : '#4caf50', flexShrink: 0 }} />
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {a.from_regime && (
                  <>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: REGIME_CLR[a.from_regime] || '#666' }}>{a.from_regime}</Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>→</Typography>
                  </>
                )}
                <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: REGIME_CLR[a.to_regime] || '#666' }}>{a.to_regime}</Typography>
                <Typography sx={{ fontSize: '0.55rem', color: '#cbd5e1' }}>{a.date}</Typography>
              </Box>
            </Box>
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}


/* ═══════════════════════════════════════════════════
   SKELETON
   ═══════════════════════════════════════════════════ */
function BriefSkeleton() {
  return (
    <Box sx={{ px: 2, py: 2, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 1.5 }} />
        <Box>
          <Skeleton width={180} height={28} />
          <Skeleton width={100} height={14} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        {[1, 2, 3].map(i => (
          <Card key={i} elevation={0} sx={{ flex: 1, ...CARD }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton width={120} height={20} sx={{ mb: 1 }} />
              <Skeleton width={80} height={28} sx={{ mb: 1 }} />
              <Skeleton width={160} height={14} />
            </CardContent>
          </Card>
        ))}
      </Box>
      <Card elevation={0} sx={{ mb: 2, ...CARD }}>
        <CardContent sx={{ py: 1.25, px: 2 }}>
          <Skeleton width="100%" height={24} sx={{ borderRadius: '6px' }} />
        </CardContent>
      </Card>
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Card elevation={0} sx={{ flex: '1 1 55%', ...CARD }}>
          <CardContent sx={{ p: 2 }}>
            {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} height={36} sx={{ mb: 0.5 }} />)}
          </CardContent>
        </Card>
        <Box sx={{ flex: '1 1 45%', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Card elevation={0} sx={CARD}><CardContent sx={{ p: 2 }}><Skeleton height={80} /></CardContent></Card>
          <Card elevation={0} sx={CARD}><CardContent sx={{ p: 2 }}><Skeleton height={60} /></CardContent></Card>
        </Box>
      </Box>
    </Box>
  );
}
